// Changement de mot de passe pour un utilisateur connecté (logique pure,
// partagée par relais-front et poseurs-front).
//
// Le validateur renvoie des CODES neutres — chaque app les mappe à sa propre
// présentation (chaînes FR en dur côté relais, clés i18n côté poseurs). La
// présentation vit donc dans les composants, pas ici : ce module reste pur et
// testable sans React ni vrai client Supabase.

export type PasswordValidationError = "empty" | "same" | "mismatch";

export function validatePasswordChange(input: {
  current: string;
  next: string;
  confirm: string;
}): PasswordValidationError | null {
  if (!input.next) return "empty";
  if (input.next === input.current) return "same";
  if (input.next !== input.confirm) return "mismatch";
  return null;
}

// Interface structurelle minimale : on ne dépend que des deux méthodes Supabase
// Auth utilisées, ce qui rend la fonction testable sans vrai client (DIP).
export interface PasswordAuthClient {
  auth: {
    signInWithPassword(credentials: {
      email: string;
      password: string;
    }): Promise<{ error: { message: string; status?: number } | null }>;
    updateUser(attributes: {
      password: string;
    }): Promise<{ error: { message: string; status?: number } | null }>;
  };
}

// Un statut absent, 0, ou ≥ 500 dénote une panne transitoire (réseau, gateway),
// pas un refus métier : on la traite comme `network` plutôt que d'exposer le
// message serveur brut.
function isTransientStatus(status: number | undefined): boolean {
  return status === undefined || status === 0 || status >= 500;
}

type UpdateOutcome =
  | { ok: true }
  | { ok: false; code: "update_failed"; message: string }
  | { ok: false; code: "network" };

// updateUser + mapping partagé par changePassword et resetPassword.
async function applyPasswordUpdate(
  client: PasswordAuthClient,
  next: string,
): Promise<UpdateOutcome> {
  const update = await client.auth.updateUser({ password: next });
  if (update.error) {
    if (isTransientStatus(update.error.status)) return { ok: false, code: "network" };
    return { ok: false, code: "update_failed", message: update.error.message };
  }
  return { ok: true };
}

export type ChangePasswordResult =
  | { ok: true }
  | { ok: false; code: "wrong_current" }
  | { ok: false; code: "update_failed"; message: string }
  | { ok: false; code: "network" };

// Supabase n'a pas de vérification native du mot de passe actuel : on la
// reconstitue en ré-authentifiant AVANT de mettre à jour. Sans ce garde, une
// session ouverte suffirait à changer le mot de passe sans connaître l'ancien.
//
// Cette fonction ne rejette JAMAIS : toute exception (réseau, abort, bug client)
// est capturée et renvoyée comme `{ ok: false, code: "network" }`, pour que
// l'appelant puisse toujours ré-armer son bouton (pas d'état « saving » bloqué).
export async function changePassword(
  client: PasswordAuthClient,
  params: { email: string; current: string; next: string },
): Promise<ChangePasswordResult> {
  try {
    const reauth = await client.auth.signInWithPassword({
      email: params.email,
      password: params.current,
    });
    if (reauth.error) {
      // 400 = identifiants invalides (mauvais mot de passe actuel). Tout autre
      // statut (429 rate-limit, 5xx) est transitoire : ne pas accuser à tort le
      // mot de passe de l'utilisateur.
      if (reauth.error.status !== undefined && reauth.error.status !== 400) {
        return { ok: false, code: "network" };
      }
      return { ok: false, code: "wrong_current" };
    }

    return await applyPasswordUpdate(client, params.next);
  } catch {
    return { ok: false, code: "network" };
  }
}

export type PasswordResetError = "empty" | "mismatch";

export function validatePasswordReset(input: {
  next: string;
  confirm: string;
}): PasswordResetError | null {
  // `.trim()` : un mot de passe uniquement composé d'espaces est traité comme
  // vide (sinon il passerait le garde et serait envoyé au serveur).
  if (!input.next.trim()) return "empty";
  if (input.next !== input.confirm) return "mismatch";
  return null;
}

export type ResetPasswordResult = UpdateOutcome;

// Réinitialisation via session recovery : la session (issue du lien mail) EST
// la preuve d'identité, donc pas de reauth avec l'ancien mdp (l'user ne l'a pas).
//
// Comme `changePassword`, cette fonction ne rejette JAMAIS : toute exception est
// capturée et renvoyée comme `{ ok: false, code: "network" }`.
export async function resetPassword(
  client: PasswordAuthClient,
  params: { next: string },
): Promise<ResetPasswordResult> {
  try {
    return await applyPasswordUpdate(client, params.next);
  } catch {
    return { ok: false, code: "network" };
  }
}
